import React from 'react';
import { MerchantRewardHistoryItemUI, MerchantRewardHistoryFilter } from '../MerchantRewardHistory.data';
import { MERCHANT_REWARD_HISTORY_FILTER_OPTIONS } from '../MerchantRewardHistory.const';
import StablePopoverTextFilter from '../../../../components/customs/table-header/stable-popover-text-filter';
import PopoverSingleSelectFilter from '../../../../components/customs/table-header/popover-single-select-filter';
import DefaultAvatar from '../../../../components/default-avatar';

interface MerchantRewardHistoryTableProps {
    data: MerchantRewardHistoryItemUI[];
    loading?: boolean;
    filter: MerchantRewardHistoryFilter;
    setFilter: (filter: MerchantRewardHistoryFilter | ((prev: MerchantRewardHistoryFilter) => MerchantRewardHistoryFilter)) => void;
}

const MerchantRewardHistoryTable: React.FC<MerchantRewardHistoryTableProps> = ({
    data,
    loading = false,
    filter,
    setFilter
}) => {
    // State to manage popover open/close for each filter
    const [filterPopoverStates, setFilterPopoverStates] = React.useState<{[key: string]: boolean}>({
        user_name: false,
        reward_name: false,
        redemption_status: false
    });

    // Create wrapper functions to match the expected type signature
    const handleFilterChange = (newFilter: MerchantRewardHistoryFilter | ((prev: MerchantRewardHistoryFilter) => MerchantRewardHistoryFilter)) => {
        if (typeof newFilter === 'function') {
            setFilter(newFilter(filter));
        } else {
            setFilter(newFilter);
        }
    };

    // Handle popover open/close
    const handlePopoverChange = (filterKey: string, isOpen: boolean) => {
        setFilterPopoverStates(prev => ({
            ...prev,
            [filterKey]: isOpen
        }));
    };

    // Helper function to check if a filter is active
    const hasActiveFilter = (filterKey: keyof MerchantRewardHistoryFilter) => {
        const value = filter[filterKey];
        if (typeof value === 'string') {
            return value.trim() !== '';
        }
        return false;
    };

    // Helper function to get TableHead classes
    const getTableHeadClasses = (filterKey?: keyof MerchantRewardHistoryFilter) => {
        const baseClasses = "relative transition-colors duration-200";
        return baseClasses;
    };

    // Helper function to get text color based on filter state
    const getTableHeadTextClasses = (filterKey?: keyof MerchantRewardHistoryFilter) => {
        if (filterKey && hasActiveFilter(filterKey)) {
            return "text-blue-700 dark:text-blue-300 font-medium";
        }
        return "";
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Redeemed':
                return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
            case 'Used':
                return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300';
            case 'Viewed':
                return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300';
            case 'Failed':
                return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
            default:
                return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 dark:border-emerald-400"></div>
            </div>
        );
    }

    return (
        <div className="w-full font-nunito">
            <table className="w-full border-collapse">
                <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${getTableHeadClasses()}`}>Image</th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${getTableHeadClasses('user_name')}`}>
                            <div className="flex items-center">
                                <span className={getTableHeadTextClasses('user_name')}>
                                    User
                                </span>
                                <StablePopoverTextFilter
                                    filterState={{
                                        filterKey: 'user_name',
                                        filter: filter,
                                        setFilter: handleFilterChange
                                    }}
                                    placeholder="Search by user name..."
                                    title="FILTER BY USER"
                                    description="Search by user name only"
                                    isOpen={filterPopoverStates.user_name}
                                    onOpenChange={(open) => handlePopoverChange('user_name', open)}
                                />
                            </div>
                        </th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${getTableHeadClasses('reward_name')}`}>
                            <div className="flex items-center">
                                <span className={getTableHeadTextClasses('reward_name')}>
                                    Reward Name
                                </span>
                                <StablePopoverTextFilter
                                    filterState={{
                                        filterKey: 'reward_name',
                                        filter: filter,
                                        setFilter: handleFilterChange
                                    }}
                                    placeholder="Search by reward name..."
                                    title="FILTER BY REWARD NAME"
                                    description="Search by reward name"
                                    isOpen={filterPopoverStates.reward_name}
                                    onOpenChange={(open) => handlePopoverChange('reward_name', open)}
                                />
                            </div>
                        </th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${getTableHeadClasses()}`}>Date Redeemed</th>
                        <th className={`h-12 px-4 text-left align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${getTableHeadClasses()}`}>Points Used</th>
                        <th className={`h-12 px-4 text-center align-middle font-medium text-muted-foreground text-sm sticky top-[100px] bg-white dark:bg-gray-900 z-20 shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${getTableHeadClasses('redemption_status')}`}>
                            <div className="flex items-center justify-center">
                                <span className={getTableHeadTextClasses('redemption_status')}>
                                    Redemption Status
                                </span>
                                <PopoverSingleSelectFilter
                                    filterState={{
                                        filterKey: 'redemption_status',
                                        filter: filter,
                                        setFilter: handleFilterChange
                                    }}
                                    options={[...MERCHANT_REWARD_HISTORY_FILTER_OPTIONS]}
                                    placeholder="Select status"
                                    title="FILTER BY STATUS"
                                    isOpen={filterPopoverStates.redemption_status}
                                    onOpenChange={(open) => handlePopoverChange('redemption_status', open)}
                                />
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item) => {
                        return (
                        <tr key={item.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="py-4 px-2">
                                {item.user?.avatar ? (
                                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                        <img src={item.user.avatar} alt={item.user?.name || ''} className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80" />
                                    </div>
                                ) : (
                                    <DefaultAvatar size={40} />
                                )}
                            </td>
                            <td className="py-4 px-2">
                                <div>
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 font-nunito text-sm">{item.user?.name || ''}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 font-nunito">{item.user?.email || ''}</div>
                                </div>
                            </td>
                            <td className="py-4 px-2">
                                <div className="font-semibold text-gray-900 dark:text-gray-100 font-nunito text-sm">{item.reward?.name || ''}</div>
                            </td>
                            <td className="py-4 px-2 text-gray-600 dark:text-gray-300 font-nunito text-sm">{item.dateRedeemed || ''}</td>
                            <td className="py-4 px-2 text-gray-600 dark:text-gray-300 font-nunito font-bold text-sm">{item.pointsUsed || 0} pts</td>
                            <td className="py-4 px-2 flex items-center justify-center min-h-[73px]">
                                <p className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold font-nunito ${getStatusColor(item.status || '')}`}>
                                    {item.status || ''}
                                </p>
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default MerchantRewardHistoryTable;

